import { useEffect, useCallback } from 'react';

function BodyAnalyzer({ image, onAnalysisComplete, onLoadingChange }) {
  
  const analyzeSkinTone = useCallback((img) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 50;
    canvas.height = 50;
    
    ctx.drawImage(img, 0, 0, img.width * 0.5, img.height * 0.3, 0, 0, 50, 50);
    
    const imageData = ctx.getImageData(0, 0, 50, 50);
    const data = imageData.data;
    
    let r = 0, b = 0;
    for (let i = 0; i < data.length; i += 4) {
      r += data[i];
      b += data[i + 2];
    }
    
    const count = data.length / 4;
    r = r / count;
    b = b / count;
    
    const warmth = (r > b) ? 'warm' : (b > r + 20) ? 'cool' : 'neutral';
    
    if (warmth === 'warm') return 'Warm';
    if (warmth === 'cool') return 'Cool';
    return 'Neutral';
  }, []);

  const getColorCombinations = useCallback((skinTone) => {
    const combos = {
      'Warm': [
        { main: 'Navy', accent: 'Mustard', mainHex: '#1e3a5f', accentHex: '#ffdb58' },
        { main: 'Olive', accent: 'Cream', mainHex: '#808000', accentHex: '#fffdd0' },
        { main: 'Brown', accent: 'Orange', mainHex: '#8b4513', accentHex: '#ff8c00' },
        { main: 'Burgundy', accent: 'Gold', mainHex: '#800020', accentHex: '#ffd700' }
      ],
      'Cool': [
        { main: 'Charcoal', accent: 'Silver', mainHex: '#36454f', accentHex: '#c0c0c0' },
        { main: 'Navy', accent: 'Pink', mainHex: '#000080', accentHex: '#ffc0cb' },
        { main: 'Black', accent: 'White', mainHex: '#000000', accentHex: '#ffffff' },
        { main: 'Plum', accent: 'Gray', mainHex: '#8e4585', accentHex: '#808080' }
      ],
      'Neutral': [
        { main: 'Black', accent: 'Tan', mainHex: '#000000', accentHex: '#d2b48c' },
        { main: 'Navy', accent: 'Coral', mainHex: '#000080', accentHex: '#ff7f50' },
        { main: 'Gray', accent: 'Yellow', mainHex: '#808080', accentHex: '#ffff00' },
        { main: 'White', accent: 'Denim', mainHex: '#ffffff', accentHex: '#1560bd' }
      ]
    };
    return combos[skinTone] || combos['Neutral'];
  }, []);

  const getBodyRecommendations = useCallback((bodyShape, skinTone) => {
    const occasions = {
      gym: {
        title: 'Gym & Activewear',
        icon: '💪',
        outfits: [
          { name: 'Fitted Tank + Joggers', desc: 'Shows muscle definition, breathable fabric', emoji: '🎽' },
          { name: 'Compression Shirt + Shorts', desc: 'Supports movement, moisture-wicking', emoji: '🩳' },
          { name: 'Sports Bra + Leggings', desc: 'High support, flexible fit', emoji: '🧘' },
          { name: 'Performance Tee + Track Pants', desc: 'Loose comfort for cardio', emoji: '👟' }
        ],
        colors: skinTone === 'Warm' ? ['Olive green', 'Rust orange', 'Cream', 'Brown'] :
                skinTone === 'Cool' ? ['Navy blue', 'Burgundy', 'Gray', 'Black'] :
                ['Charcoal', 'Teal', 'White', 'Navy']
      },
      party: {
        title: 'Party & Night Out',
        icon: '🎉',
        outfits: [
          { name: 'Blazer + Dark Jeans', desc: 'Smart casual, structured look', emoji: '🕴️' },
          { name: 'Cocktail Dress + Heels', desc: 'Elegant, shows off silhouette', emoji: '👗' },
          { name: 'Leather Jacket + Slim Pants', desc: 'Edgy, confident style', emoji: '🧥' },
          { name: 'Sequin Top + Trousers', desc: 'Glamorous yet comfortable', emoji: '✨' }
        ],
        colors: skinTone === 'Warm' ? ['Gold', 'Emerald', 'Deep red', 'Champagne'] :
                skinTone === 'Cool' ? ['Silver', 'Royal blue', 'Plum', 'Black'] :
                ['Rose gold', 'Sapphire', 'Wine', 'Charcoal']
      },
      casual: {
        title: 'Casual Everyday',
        icon: '☕',
        outfits: [
          { name: 'Well-fitted T-shirt + Jeans', desc: 'Classic, versatile foundation', emoji: '👕' },
          { name: 'Button-up + Chinos', desc: 'Polished yet relaxed', emoji: '👔' },
          { name: 'Sweater + Straight Pants', desc: 'Cozy, put-together look', emoji: '🧶' },
          { name: 'Polo + Khakis', desc: 'Preppy, timeless style', emoji: '🏌️' }
        ],
        colors: skinTone === 'Warm' ? ['Beige', 'Terracotta', 'Sage', 'Cream'] :
                skinTone === 'Cool' ? ['Light blue', 'Lavender', 'Mint', 'Gray'] :
                ['Taupe', 'Dusty rose', 'Olive', 'White']
      },
      formal: {
        title: 'Formal & Business',
        icon: '💼',
        outfits: [
          { name: 'Tailored Suit + Dress Shirt', desc: 'Professional power look', emoji: '🤵' },
          { name: 'Pencil Skirt + Blouse', desc: 'Sharp, feminine silhouette', emoji: '👚' },
          { name: 'Trench Coat + Dress Pants', desc: 'Sophisticated layers', emoji: '🧥' },
          { name: 'Blazer Dress + Pumps', desc: 'Modern executive style', emoji: '👠' }
        ],
        colors: skinTone === 'Warm' ? ['Navy', 'Chocolate', 'Burgundy', 'Tan'] :
                skinTone === 'Cool' ? ['Charcoal', 'Black', 'Navy', 'Silver'] :
                ['Black', 'Navy', 'Gray', 'White']
      },
      beach: {
        title: 'Beach & Vacation',
        icon: '🏖️',
        outfits: [
          { name: 'Linen Shirt + Shorts', desc: 'Breathable, relaxed elegance', emoji: '🌴' },
          { name: 'Swim Trunks + Tank', desc: 'Easy beach-to-bar transition', emoji: '🩱' },
          { name: 'Maxi Dress + Sandals', desc: 'Flowing, comfortable chic', emoji: '👡' },
          { name: 'Hawaiian Shirt + Chinos', desc: 'Tropical, fun vibe', emoji: '🌺' }
        ],
        colors: skinTone === 'Warm' ? ['Coral', 'Turquoise', 'White', 'Sand'] :
                skinTone === 'Cool' ? ['Aqua', 'Cobalt', 'White', 'Navy'] :
                ['Teal', 'Peach', 'White', 'Khaki']
      }
    };

    const bodySpecificTips = {
      'Hourglass': 'Emphasize your waist with belts and fitted styles. Avoid boxy shapes that hide your curves.',
      'Rectangle': 'Create curves with peplum tops, layered looks, and belts. Ruffles and patterns add dimension.',
      'Pear': 'Highlight your shoulders with boat necks and statement sleeves. A-line skirts balance your hips.',
      'Apple': 'Draw eyes up with V-necks and statement jewelry. Empire waists and straight-leg pants work best.',
      'Inverted Triangle': 'Add volume to hips with flared pants and A-line skirts. Soften shoulders with draped fabrics.'
    };

    return {
      occasions,
      bodyTips: bodySpecificTips[bodyShape] || 'Choose fitted styles that make you feel confident.',
      colorCombinations: getColorCombinations(skinTone)
    };
  }, [getColorCombinations]);

  useEffect(() => {
    const analyzeBody = async () => {
      onLoadingChange(true);
      
      try {
        const img = new Image();
        img.src = image;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        const aspectRatio = img.width / img.height;
        const area = img.width * img.height;
        
        let bodyShape, characteristics, confidence;
        
        if (aspectRatio > 0.8 && aspectRatio < 1.2) {
          if (area > 400000) {
            bodyShape = 'Hourglass';
            characteristics = 'Your shoulders and hips are balanced with a defined waist. This classic shape suits fitted styles that highlight your curves.';
            confidence = 'high';
          } else if (area > 250000) {
            bodyShape = 'Rectangle';
            characteristics = 'Your shoulders, waist, and hips are fairly uniform. This athletic shape looks great in structured pieces that create curves.';
            confidence = 'medium';
          } else {
            bodyShape = 'Pear';
            characteristics = 'Your hips are wider than your shoulders with a defined waist. Balance your silhouette by highlighting your upper body.';
            confidence = 'medium';
          }
        } else if (aspectRatio >= 1.2) {
          bodyShape = 'Apple';
          characteristics = 'You carry weight around your midsection with slimmer legs. Draw attention upward with V-necks and structured shoulders.';
          confidence = 'medium';
        } else {
          bodyShape = 'Inverted Triangle';
          characteristics = 'Your shoulders are broader than your hips. Soften your upper body and add volume to your lower half for balance.';
          confidence = 'medium';
        }

        const skinTone = analyzeSkinTone(img);
        
        onAnalysisComplete({
          bodyShape,
          characteristics,
          confidence,
          skinTone,
          recommendations: getBodyRecommendations(bodyShape, skinTone)
        });
        
      } catch (error) {
        console.error('Body analysis error:', error);
        onAnalysisComplete({
          error: "Analysis failed. Please try again with a clearer full-body photo."
        });
      }
    };

    analyzeBody();
  }, [image, onAnalysisComplete, onLoadingChange, analyzeSkinTone, getBodyRecommendations]);

  return null;
}

export default BodyAnalyzer;